import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Stack,
} from '@mui/material';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { selectAllNews, selectNewsStatus } from '../redux/newsSlice';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const NewsDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const news = useSelector(selectAllNews);
    const status = useSelector(selectNewsStatus);
    const [article, setArticle] = useState(null);
    const loading = status === 'loading' || status === 'idle';

    useEffect(() => {
        if (status === 'succeeded' && news?.length > 0) {
            const foundArticle = news.find((item) => String(item.id) === String(id));
            if (foundArticle) {
                setArticle(foundArticle);
            } else {
                navigate('/news');
            }
        } else if (status === 'failed') {
            navigate('/news');
        }
    }, [news, id, status, navigate]);

    if (loading || !article) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#ffffff">
                <CircularProgress color="primary" />
                <Typography variant="body1" sx={{ mt: 2, fontFamily: 'Bebas Neue', letterSpacing: 1 }}>
                    Retrieving Article...
                </Typography>
            </Box>
        );
    }

    const formattedDate = article?.date ? format(new Date(article.date), 'dd MMMM yyyy') : '';

    return (
        <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', position: 'relative' }}>
            <PageHeader
                showBackButton={true}
                title={article.title}
                backgroundImage="/images/headers/news_bg.jpeg"
                metadata={[{ label: 'Published', value: formattedDate },]}
            />


            <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, position: 'relative', zIndex: 1 }}>
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <Box
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(20px)',
                            p: { xs: 3, md: 6 },
                            borderRadius: '32px',
                            boxShadow: '0 30px 60px rgba(0,0,0,0.05)',
                            border: '1px solid rgba(0,0,0,0.05)',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                borderRadius: '24px',
                                overflow: 'hidden',
                                mb: 6,
                                boxShadow: '0 20px 50px rgba(0,0,0,0.1)',
                                border: '1px solid rgba(0,0,0,0.05)',
                            }}
                        >
                            <Box
                                component="img"
                                src={article.imageUrl}
                                alt={article.title}
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '600px',
                                    objectFit: "contain",
                                    display: 'block'
                                }}
                            />
                        </Box>

                        <Stack spacing={4}>
                            {article.content?.split('\n').filter(Boolean).map((paragraph, index) => (
                                <Typography
                                    key={index}
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                                        lineHeight: 1.8,
                                        color: '#334155',
                                        fontWeight: 400,
                                        letterSpacing: '0.01em',
                                    }}
                                >
                                    {paragraph}
                                </Typography>
                            ))}
                        </Stack>

                        <Box sx={{ mt: 8, pt: 4, borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'center' }}>
                            <Typography
                                variant="overline"
                                sx={{
                                    color: '#94a3b8',
                                    fontWeight: 700,
                                    letterSpacing: 4,
                                    fontFamily: 'Bebas Neue'
                                }}
                            >
                                END OF ARTICLE
                            </Typography>
                        </Box>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default NewsDetail;
